import type { ButtonHTMLAttributes } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'accent' | 'cream' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
}

/**
 * Botão pílula do Bow Redesign (rounded-full).
 *  - primary: navy fill / cream text — CTA principal.
 *  - secondary: outline navy translúcido — CTA secundário.
 *  - accent: emerald fill — sucesso / pague-com-PIX.
 *  - cream: white fill — uso sobre fundo navy/colorido.
 *  - ghost: transparent — texto-link grande.
 *  - danger: outline vermelho — encerrar recarga / sair / excluir.
 *
 * O default `secondary` antigo (navy fill) virou `primary` no redesign;
 * mantemos os nomes pra evitar quebra mas a aparência mudou.
 */
const VARIANT: Record<Variant, string> = {
  primary:
    'bg-bow-royal text-bow-cream hover:bg-bow-royal/90 active:bg-bow-royal/85 disabled:opacity-50',
  secondary:
    'bg-transparent text-bow-royal border-[1.5px] border-bow-royal/20 hover:bg-bow-royal/5 disabled:opacity-50',
  accent: 'bg-bow-green text-white hover:bg-bow-green/90 disabled:opacity-50',
  cream: 'bg-white text-bow-royal hover:bg-bow-cream-2 disabled:opacity-50',
  ghost: 'bg-transparent text-bow-royal hover:bg-bow-royal/5 disabled:opacity-50',
  danger:
    'bg-transparent text-bow-red border-[1.5px] border-bow-red/30 hover:bg-bow-red/5 disabled:opacity-50'
};

const SIZE: Record<Size, string> = {
  sm: 'h-9 px-4 text-[13px]',
  md: 'h-11 px-5 text-sm',
  lg: 'h-[54px] px-6 text-base'
};

export function Button({
  variant = 'primary',
  size = 'lg',
  fullWidth = true,
  className = '',
  ...rest
}: Props) {
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-full font-semibold tracking-tight transition disabled:cursor-not-allowed ${SIZE[size]} ${VARIANT[variant]} ${fullWidth ? 'w-full' : ''} ${className}`}
      {...rest}
    />
  );
}
